import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/config';
import useStore from '../store/useStore';

export default function LoginPage() {
  const navigate = useNavigate();
  const { setUser, setToken, darkMode, toggleDarkMode } = useStore();
  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [otp, setOtp] = useState(['','','','','','']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const inputRefs = useRef([]);

  const startTimer = () => {
    setResendTimer(60);
    const iv = setInterval(() => setResendTimer(t => { if(t<=1){clearInterval(iv);return 0;} return t-1; }), 1000);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error('Please fill all fields');
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/login-2fa`, { email, password });
      toast.success('OTP sent to your email!');
      setStep('otp');
      startTimer();
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    } catch(err) {
      toast.error(err.response?.data?.message || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) return toast.error('Enter complete 6-digit OTP');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/auth/login-verify-otp`, { email, otp: code });
      setToken(data.token);
      setUser(data.user);
      toast.success(`Welcome, ${data.user.full_name}! 👋`);
      navigate(data.user.profile_complete ? '/chat' : '/profile-setup');
    } catch(err) {
      toast.error(err.response?.data?.message || 'Invalid OTP');
      setOtp(['','','','','','']);
      inputRefs.current[0]?.focus();
    } finally { setLoading(false); }
  };

  const handleOtpChange = (i, val) => {
    if (!/^\d*$/.test(val)) return;
    const n = [...otp]; n[i] = val.slice(-1); setOtp(n);
    if (val && i < 5) inputRefs.current[i+1]?.focus();
  };

  const handleOtpKey = (i, e) => {
    if (e.key === 'Backspace' && !otp[i] && i > 0) inputRefs.current[i-1]?.focus();
  };

  const handleOtpPaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
    if (paste.length === 6) { setOtp(paste.split('')); inputRefs.current[5]?.focus(); }
  };

  const handleResend = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/auth/login-2fa`, { email, password });
      toast.success('New OTP sent!');
      setOtp(['','','','','','']);
      startTimer();
      inputRefs.current[0]?.focus();
    } catch(err) { toast.error('Failed to resend'); }
    finally { setLoading(false); }
  };

  return (
    <div className={`fixed inset-0 flex ${darkMode?'dark':''}`}>

      {/* ── LEFT — gradient brand panel ── */}
      <div className="hidden lg:flex w-5/12 flex-col items-center justify-center relative overflow-hidden"
        style={{background:'linear-gradient(145deg,#0d9488 0%,#0369a1 45%,#1d4ed8 100%)'}}>

        {/* Animated blobs */}
        <div className="absolute w-96 h-96 rounded-full opacity-20 blur-3xl"
          style={{background:'radial-gradient(circle,#ffffff,transparent)',top:'-10%',left:'-10%'}}/>
        <div className="absolute w-80 h-80 rounded-full opacity-15 blur-3xl"
          style={{background:'radial-gradient(circle,#a5f3fc,transparent)',bottom:'-5%',right:'-10%'}}/>

        {/* Subtle mesh grid */}
        <div className="absolute inset-0"
          style={{
            backgroundImage:'linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)',
            backgroundSize:'40px 40px'
          }}/>

        {/* Logo */}
        <div className="relative z-10 flex flex-col items-center">
          <div className="flex flex-col items-center">
            <img src="/logo.png" alt="Qtalk"
              className="w-52 object-contain mb-2"
              style={{filter:'brightness(0) invert(1)'}}/>
          </div>
        </div>

        {/* Bottom copyright */}
        <p className="absolute bottom-6 text-white/25 text-xs">© 2026 QualTechEdge · Internal Use Only</p>
      </div>

      {/* ── RIGHT — form panel ── */}
      <div className="flex-1 flex flex-col items-center justify-center relative px-6"
        style={{
          background: darkMode
            ? 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)'
            : 'linear-gradient(135deg,#f8fafc 0%,#f0fdf9 50%,#eff6ff 100%)'
        }}>

        {/* Subtle top-right glow */}
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full opacity-10 blur-3xl pointer-events-none"
          style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}/>

        {/* Dark mode toggle */}
        <button onClick={toggleDarkMode}
          className="absolute top-5 right-5 p-2.5 rounded-2xl transition shadow-sm"
          style={{
            background: darkMode?'rgba(255,255,255,0.07)':'rgba(0,0,0,0.05)',
            border: darkMode?'1px solid rgba(255,255,255,0.1)':'1px solid rgba(0,0,0,0.07)'
          }}>
          {darkMode
            ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-yellow-400"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
            : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-gray-500"><path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/></svg>}
        </button>

        {/* Mobile logo */}
        <div className="lg:hidden flex items-center gap-2 mb-8">
          <img src="/logo.png" alt="Qtalk" className="w-8 h-8 object-contain"/>
          <span className="font-black text-lg" style={{color:darkMode?'white':'#111827'}}>Qtalk</span>
        </div>

        <div className="w-full max-w-sm relative z-10">

          {/* ── Credentials step ── */}
          {step === 'credentials' && (
            <>
              <div className="mb-8">
                <h1 className="text-3xl font-black tracking-tight mb-1"
                  style={{color:darkMode?'white':'#0f172a'}}>Welcome back</h1>
                <p className="text-sm" style={{color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8'}}>
                  Sign in to your Qtalk account
                </p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                {/* Email */}
                <div>
                  <label className="text-xs font-bold tracking-widest uppercase block mb-2"
                    style={{color:darkMode?'rgba(255,255,255,0.35)':'#94a3b8'}}>Email</label>
                  <input type="email" value={email} onChange={e=>setEmail(e.target.value)}
                    placeholder="you@qualtechedge.com" autoFocus
                    className="w-full px-4 py-3.5 rounded-2xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
                    style={{
                      background: darkMode?'rgba(255,255,255,0.06)':'white',
                      border: darkMode?'1.5px solid rgba(255,255,255,0.08)':'1.5px solid #e2e8f0',
                      color: darkMode?'white':'#0f172a',
                      boxShadow: darkMode?'none':'0 1px 3px rgba(0,0,0,0.04)'
                    }}/>
                </div>

                {/* Password */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-xs font-bold tracking-widest uppercase"
                      style={{color:darkMode?'rgba(255,255,255,0.35)':'#94a3b8'}}>Password</label>
                    <Link to="/forgot-password" className="text-xs font-semibold text-teal-500 hover:text-teal-400 transition">Forgot?</Link>
                  </div>
                  <div className="relative">
                    <input type={showPass?'text':'password'} value={password} onChange={e=>setPassword(e.target.value)}
                      placeholder="Enter your password"
                      className="w-full px-4 py-3.5 pr-12 rounded-2xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-teal-400 transition"
                      style={{
                        background: darkMode?'rgba(255,255,255,0.06)':'white',
                        border: darkMode?'1.5px solid rgba(255,255,255,0.08)':'1.5px solid #e2e8f0',
                        color: darkMode?'white':'#0f172a',
                        boxShadow: darkMode?'none':'0 1px 3px rgba(0,0,0,0.04)'
                      }}/>
                    <button type="button" onClick={()=>setShowPass(!showPass)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 transition"
                      style={{color:darkMode?'rgba(255,255,255,0.25)':'#cbd5e1'}}>
                      {showPass
                        ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading}
                  className="w-full py-4 rounded-2xl text-white font-black text-sm tracking-wide transition hover:opacity-90 disabled:opacity-50"
                  style={{
                    background:'linear-gradient(135deg,#0d9488 0%,#2563eb 100%)',
                    boxShadow:'0 8px 24px rgba(13,148,136,0.35)'
                  }}>
                  {loading
                    ? <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                        Verifying...
                      </span>
                    : 'Sign In →'}
                </button>
              </form>

              <p className="text-center text-sm mt-6" style={{color:darkMode?'rgba(255,255,255,0.3)':'#94a3b8'}}>
                New to Qtalk?{' '}
                <Link to="/register" className="text-teal-500 font-bold hover:text-teal-400 transition">Create account</Link>
              </p>
            </>
          )}

          {/* ── OTP step ── */}
          {step === 'otp' && (
            <>
              <button onClick={()=>{setStep('credentials');setOtp(['','','','','','']);}}
                className="flex items-center gap-1.5 text-sm mb-8 transition"
                style={{color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8'}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><polyline points="15 18 9 12 15 6"/></svg>
                Back
              </button>

              <div className="mb-6">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{background:'linear-gradient(135deg,#0d9488,#2563eb)'}}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
                  </svg>
                </div>
                <h1 className="text-2xl font-black tracking-tight mb-1"
                  style={{color:darkMode?'white':'#0f172a'}}>Check your email</h1>
                <p className="text-sm" style={{color:darkMode?'rgba(255,255,255,0.4)':'#94a3b8'}}>
                  Code sent to <span className="font-bold text-teal-500">{email}</span>
                </p>
              </div>

              <form onSubmit={handleVerifyOtp}>
                <div className="flex gap-2 justify-between mb-6" onPaste={handleOtpPaste}>
                  {otp.map((digit,i) => (
                    <input key={i} ref={el=>inputRefs.current[i]=el}
                      type="text" inputMode="numeric" maxLength={1} value={digit}
                      onChange={e=>handleOtpChange(i,e.target.value)}
                      onKeyDown={e=>handleOtpKey(i,e)}
                      className="w-11 h-12 text-center text-xl font-black rounded-xl focus:outline-none transition-all"
                      style={{
                        background: digit
                          ? darkMode?'rgba(13,148,136,0.2)':'rgba(13,148,136,0.08)'
                          : darkMode?'rgba(255,255,255,0.06)':'white',
                        border: digit?'2px solid #0d9488':darkMode?'2px solid rgba(255,255,255,0.08)':'2px solid #e2e8f0',
                        color: darkMode?'white':'#0f172a',
                        boxShadow: digit?'0 0 0 3px rgba(13,148,136,0.15)':'none',
                        transform: digit?'scale(1.03)':'scale(1)'
                      }}/>
                  ))}
                </div>

                <button type="submit" disabled={loading||otp.join('').length!==6}
                  className="w-full py-4 rounded-2xl text-white font-black text-sm tracking-wide transition hover:opacity-90 disabled:opacity-40"
                  style={{
                    background:'linear-gradient(135deg,#0d9488,#2563eb)',
                    boxShadow:'0 8px 24px rgba(13,148,136,0.35)'
                  }}>
                  {loading
                    ? <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
                        Verifying...
                      </span>
                    : 'Verify & Sign In →'}
                </button>
              </form>

              <div className="text-center mt-5">
                {resendTimer>0
                  ? <p className="text-sm" style={{color:darkMode?'rgba(255,255,255,0.3)':'#94a3b8'}}>
                      Resend in <span className="font-bold text-teal-500 tabular-nums">{resendTimer}s</span>
                    </p>
                  : <button onClick={handleResend} disabled={loading}
                      className="text-sm font-bold text-teal-500 hover:text-teal-400 transition">
                      Resend OTP
                    </button>
                }
              </div>

              <div className="flex items-center justify-center gap-2 mt-8 pt-6"
                style={{borderTop:darkMode?'1px solid rgba(255,255,255,0.06)':'1px solid #f1f5f9'}}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-teal-500">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                <span className="text-xs font-medium text-teal-500">Protected by 2-factor authentication</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
